<template>
  <section
    class="construction-params"
    :class="{ 'construction-params--with-filling': sector.filling }"
  >
    <IsolationMaterials v-if="hasIsolation"></IsolationMaterials>
    <article class="construction-params__forms">
      <h2 class="wizard__title">
        <span>{{ $t('message.wizard.titles.constructionParams') }}</span>
      </h2>
      <template v-if="baseParams">
        <Structure
          :params="baseParams"
          type="base"
          :sector="sector"
          :visible="true"
          @defineValidity="defineValidity($event)"
        >
          <h5 class="construction-params__title">
            {{ $t('message.constructionParams.params.title') }}
          </h5>
        </Structure>
        <Structure
          v-if="screedParams"
          :params="screedParams"
          type="screed"
          :sector="sector"
          :visible="true"
          @defineValidity="defineValidity($event)"
        />
      </template>
    </article>
    <Index :sectorIndexes="sector.indexes" v-if="readyParams || !baseParams" :sector="sector" />
    <template>
      <Recap :indexes="sector.indexes" v-if="showAdvices" :sector="sector" />
      <ErrorNotification
        v-if="isGettingErrors"
        :notifications="errorNotifications"
        :constructionType="sector.constructionType.type"
      />
      <Constructions
        v-if="isThereOptionalSystem && !isGettingErrors && (readyParams || !baseParams)"
        :constructions="sector.optionalSystems"
        :selectedConstructions="sector.construction"
        @handle="setActiveConstruction($event)"
      />
    </template>
  </section>
</template>

<script>
import Index from '@/components/dump/IndexComponent'
import Structure from '@/components/smart/ConstructionStructure'
import { mapActions, mapGetters } from 'vuex'
import Constructions from '@/components/smart/Constructions'
import { getSuitableSystems } from '@/api'
import { CalcTypes, ConstructionTypes, SizeParams, Structures } from '@/utils/types'
import ErrorNotification from '@/components/dump/ErrorNotification.vue'
import DropdownConfigsMixin from '@/components/mixin/DropdownConfigsMixin.vue'
import Recap from '@/components/dump/RecapComponent.vue'
import IsolationMaterials from '@/components/smart/IsolationMaterials.vue'

export default {
  name: 'ConstructionParams',
  props: ['sector'],
  mixins: [DropdownConfigsMixin],
  components: { IsolationMaterials, Recap, Constructions, Index, Structure, ErrorNotification },
  watch: {
    selectedOptionalSystem: function (newValue) {
      if (newValue !== null && !this.hasSizesBlock) { this.getSuitableSystems(this.selectedOptionalSystem) }
    },
    readyParams: function (newValue, oldValue) {
      if (oldValue && !newValue) {
        this.updateSectorParams('isCalculated', false)
      }
    },
    base: function () {
      this.reset()
    },
    screed: function () {
      this.reset()
    },
    facing: function () {
      this.reset()
    },
    isolation: function () {
      this.reset()
    },
    soundIsolation: function () {
      this.reset()
    },
    sizeParams: function () {
      this.reset()
    },
    facingInnerLayer: function () {
      this.reset()
    },
    getSelectedIsolationType: function (newValue, oldValue) {
      if ((oldValue !== newValue) && oldValue !== null) {
        this.getOptionalSystemBySector()
        this.reset()
      }
    }
  },
  mounted() {
    this.getOptionalSystemBySector()
  },
  computed: {
    ...mapGetters({
      getConstructionTypes: 'getConstructionTypes',
      sizesGetter: 'sizesGetter',
      getSelectedIsolationType: 'getSelectedIsolationType',
      getIsolationPreSelected: 'getIsolationPreSelected'
    }),
    sectorIndex() {
      return this.$store.state.sectors.findIndex(sector => sector.id === this.sector.id)
    },
    errorNotifications() {
      return this.sector.errors
    },
    selectedOptionalSystem() {
      return this.sector.construction
    },
    constructionStructure() {
      return this.sector.structures
    },
    baseParams() {
      return this.getStructure(this.$t('message.constructionParams.params.base'))
    },
    screedParams() {
      return this.getStructure(this.$t('message.constructionParams.params.screed'))
    },
    facingParams() {
      return this.getStructure(this.$t('message.constructionParams.facingParams.constructionGroup'))
    },
    isolationParams() {
      return this.getStructure(
        this.$t('message.constructionParams.isolationParams.constructionGroup')
      )
    },
    soundIsolationParams() {
      return this.getStructure(
        this.$t('message.constructionParams.soundIsolationParams.constructionGroup')
      )
    },
    sizes() {
      return this.getStructure(this.$t('message.constructionParams.sizeParams.constructionGroup'))
    },
    base() {
      return this.sector.base
    },
    screed() {
      return this.sector.screed
    },
    facing() {
      return this.sector.facing
    },
    isolation() {
      return this.sector.isolation
    },
    soundIsolation() {
      return this.sector.soundIsolation
    },
    sizeParams() {
      return this.sector.sizes
    },
    facingInnerLayer() {
      return this.sector.facingInnerLayer
    },
    isFloor() {
      return this.currentConstructionType === ConstructionTypes.floor
    },
    readyParams() {
      const method = () => this.getOptionalSystem(this.sectorIndex)
      const isReady = !this.hasUnfilledParams() && !this.hasUnfilledSizesParams()

      if (isReady && this.sector.isValid) {
        method()
      }

      return isReady
    },
    isThereOptionalSystem() {
      return this.sector.optionalSystems?.length
    },
    currentConstructionType() {
      return this.sector.constructionType.type
    },
    isGettingErrors() {
      return this.errorNotifications
        ? Object.values(this.errorNotifications).some(error => error.needRender)
        : false
    },
    hasSizesBlock() {
      return this.sector.system?.isSizeable
    },
    hasIsolation() {
      return this.sector.system?.isIsolated && !this.getIsolationPreSelected
    },
    showAdvices() {
      return this.sector.isCalculated && this.hasSizesBlock
    }
  },
  methods: {
    ...mapActions({
      getOptionalSystem: 'getOptionalSystem',
      setCalculatedIndexes: 'setCalculatedIndexes',
      getCalculationResult: 'getCalculationResult',
      setTestingStatus: 'setTestingStatus'
    }),
    getOptionalSystemBySector() {
      if (this.sector.structures && this.sector.structures.length === 0) {
        this.getOptionalSystem(this.sectorIndex, true)
      }
    },
    setActiveConstruction(evt) {
      this.updateSectorParams('construction', evt.value)
      this.updateSectorParams('isCalculated', true)
    },
    getSuitableSystems(system) {
      this.setCalculatedIndexes({
        index: this.sectorIndex,
        payload: { rw: system?.rw, lnw: system?.lnw }
      })
      this.setTestingStatus({ index: this.sectorIndex, payload: system?.isTested })

      const requestData = {
        optionSystemId: this.sector.construction?.id,
        rw: this.sector.indexes.calculated?.rw,
        lnw: this.sector.indexes.calculated?.lnw,
        rwRequired: this.sector.indexes.required?.rw,
        lnwRequired: this.sector.indexes.required?.lnw
      }

      getSuitableSystems(requestData).then(response => {
        const errors = {
          rw: {
            text: response.data.rw === '' ? null : response.data.rw,
            needRender: response.data.rw !== null && response.data.rw !== ''
              ? requestData.rw <= requestData.rwRequired
              : false
          },
          lnw: {
            text: response.data.lnw,
            needRender:
              response.data.lnw !== null ? requestData.lnw >= requestData.lnwRequired : false
          }
        }

        this.updateSectorParams('errors', errors)

        this.isGettingErrors
          ? this.updateSectorParams('optionalSystems', null)
          : this.updateSectorParams('isCalculated', true)
      })
    },
    updateSectorParams(property, value, subproperty = null) {
      this.$store.commit('UPDATE_SECTOR', { index: this.sectorIndex, property, subproperty, value })
    },
    resetErrors() {
      this.updateSectorParams('errors', null)
      this.updateSectorParams('image', null)
    },
    resetCalculationStatus() {
      if (this.sector.isCalculated) {
        this.updateSectorParams('isCalculated', false)
        this.updateSectorParams('indexes', null, 'calculated')
      }
    },
    reset() {
      this.updateSectorParams('construction', null)
      this.updateSectorParams('optionalSystems', null)
      this.resetCalculationStatus()
      this.resetErrors()
    },
    hasUnfilledParams() {
      const requiredParams = {
        [CalcTypes.lite]: {
          [ConstructionTypes.wall]: [Structures.base],
          [ConstructionTypes.floor]: [Structures.base, Structures.screed]
        }
      }

      let currentParamsSet = requiredParams[this.sector.algorithm][this.currentConstructionType]

      return currentParamsSet.some(
        param =>
          Object.entries(this[param]).filter(([key, value]) => !key.includes('Blocked') && !value)
            .length !== 0
      )
    },
    getStructure(name) {
      return this.constructionStructure?.find(structure => structure.constructionGroup === name)
    },
    hasUnfilledSizesParams() {
      if (!this.hasSizesBlock) {
        return false
      }

      const sizesParams = {
        [ConstructionTypes.wall]: [SizeParams.x, SizeParams.y],
        [ConstructionTypes.ceiling]: [SizeParams.x, SizeParams.y],
        [ConstructionTypes.floor]: []
      }
      const sizes = this.sizesGetter(this.sectorIndex)

      return sizesParams[this.currentConstructionType].length === 0 || sizes.sizesBlocked
        ? false
        : Object.keys(sizes)
          .filter(key => sizesParams[this.currentConstructionType].includes(key))
          .map(value => sizes[value])
          .filter(value => !value).length !== 0
    }
  }
}
</script>

<style scoped lang="sass">
.construction-params
  display: flex
  flex-wrap: wrap
  width: 100%
  &--with-filling
    .construction-params__forms
      order: 1
    .filling
      order: 2
      +media((margin-bottom: (0: rem(10), 768: 0)))
    .indexes
      order: 3
    .constructions
      order: 4
  &__title
    margin: 0 0 rem(20)
    font-weight: 600
    line-height: 1.3
  &__forms
    width: 100%
  &__forms + .indexes:has(.indexes__list:not(:empty))
    +media((margin-top: (0: rem(10), 768: rem(30))))
  .filling + .constructions
    margin-top: rem(30)

.construction-params__forms:has(.structure:last-child .dropdown.dropdown--with-tooltip:last-child) + .indexes
  +media((margin-top: (0: -30px, 768: -10px, 800: -19px, 1230: -34px)))
</style>
